import { AlertTriangle, KeyRound, UserCheck, Users } from "lucide-react";
import type { AccessSummary } from "@/hooks/use-access";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  data?: AccessSummary;
  isLoading: boolean;
}

/**
 * Resumen ejecutivo del panel: responde de un vistazo a las dos preguntas
 * (adopción y seguridad) sin tener que abrir ninguna pestaña.
 */
export function AccessSummaryCards({ data, isLoading }: Props) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  const cards = [
    {
      label: "Usuarios activos",
      value: `${data?.active_users ?? 0}`,
      hint: `de ${data?.total_users ?? 0} dados de alta`,
      icon: Users,
      tone: "text-blue-600",
    },
    {
      label: "Conectados ahora",
      value: `${data?.online_now ?? 0}`,
      hint: "actividad en los últimos 15 min",
      icon: UserCheck,
      tone: "text-green-600",
    },
    {
      label: "Nunca han entrado",
      value: `${data?.never_logged_in ?? 0}`,
      hint: "altas sin usar",
      icon: AlertTriangle,
      tone: (data?.never_logged_in ?? 0) > 0 ? "text-amber-600" : "text-gray-400",
    },
    {
      label: "Intentos fallidos",
      value: `${data?.failed_logins_30d ?? 0}`,
      hint: "últimos 30 días",
      icon: KeyRound,
      tone: (data?.failed_logins_30d ?? 0) > 0 ? "text-red-600" : "text-gray-400",
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {cards.map(({ label, value, hint, icon: Icon, tone }) => (
        <div
          key={label}
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {label}
            </p>
            <Icon className={`h-4 w-4 ${tone}`} />
          </div>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
          <p className="mt-1 text-xs text-gray-500">{hint}</p>
        </div>
      ))}
    </div>
  );
}
